"use client"
import {
  Container,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Alert,
  Paper,
  Divider,
  useTheme,
  Card,
  CardContent,
} from "@mui/material"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import BuildIcon from "@mui/icons-material/Build"
import ComputerIcon from "@mui/icons-material/Computer"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import DownloadIcon from "@mui/icons-material/Download"
import SettingsIcon from "@mui/icons-material/Settings"
import WarningIcon from "@mui/icons-material/Warning"
import SpeedIcon from "@mui/icons-material/Speed"
import config from "../config"

const VPS = () => {
  const theme = useTheme()

  const handleExternalNavigation = (url) => {
    window.open(url, "_blank", "noopener,noreferrer")
  }

  return (
    <Container maxWidth="md">
      <Paper
        elevation={3}
        sx={{
          p: 4,
          background: "rgba(13, 15, 25, 0.7)",
          border: "1px solid rgba(0, 229, 255, 0.1)",
          borderRadius: 2,
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 0 20px rgba(0, 0, 0, 0.5)",
        }}
      >
        {/* Decorative elements */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            opacity: 0.03,
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='smallGrid' width='10' height='10' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 10 0 L 0 0 0 10' fill='none' stroke='%2300e5ff' strokeWidth='0.5'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23smallGrid)'/%3E%3C/svg%3E\")",
            zIndex: 0,
          }}
        />

        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 4, gap: 2 }}>
            <ComputerIcon
              sx={{
                fontSize: 40,
                color: theme.palette.primary.main,
                textShadow: "0 0 8px rgba(0, 229, 255, 0.6)",
              }}
            />
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: "bold",
                background: "linear-gradient(45deg, #ffffff 30%, #00e5ff 90%)",
                backgroundClip: "text",
                textFillColor: "transparent",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                textShadow: "0 0 2px rgba(0, 229, 255, 0.5)",
              }}
            >
              VPS for bot
            </Typography>
          </Box>

          <Box
            sx={{
              p: 3,
              mb: 4,
              borderRadius: 2,
              background: "linear-gradient(90deg, rgba(0,229,255,0.1) 0%, rgba(0,0,0,0) 100%)",
              border: "1px solid rgba(0, 229, 255, 0.2)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Typography variant="h6" sx={{ color: theme.palette.primary.main, mb: 2 }}>
              Recommended VPS Package
            </Typography>

            <Box
              sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 3, alignItems: "center", mb: 3 }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
                  DE-3900-4GBrrr
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Our recommended package that runs exactly 1 DarkEdge instance perfectly.
                </Typography>
              </Box>

              <Button
                variant="contained"
                size="large"
                startIcon={<ShoppingCartIcon />}
                onClick={() => handleExternalNavigation(config.VPS_PROVIDER_LINK, "Buy VPS (DE-3900-4GBrrr)")}
                sx={{
                  background: "linear-gradient(45deg, #00e5ff 30%, #2979ff 90%)",
                  boxShadow: "0 0 10px rgba(0, 229, 255, 0.5)",
                  px: 3,
                  py: 1.5,
                  "&:hover": {
                    boxShadow: "0 0 15px rgba(0, 229, 255, 0.8)",
                  },
                }}
              >
                Buy VPS (DE-3900-4GBrrr)
              </Button>
            </Box>

            <List sx={{ mt: 2 }}>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon sx={{ color: theme.palette.success.main }} />
                </ListItemIcon>
                <ListItemText primary="Tested 24/7 bot" primaryTypographyProps={{ fontWeight: "medium" }} />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon sx={{ color: theme.palette.success.main }} />
                </ListItemIcon>
                <ListItemText primary="Very good performance" primaryTypographyProps={{ fontWeight: "medium" }} />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon sx={{ color: theme.palette.success.main }} />
                </ListItemIcon>
                <ListItemText
                  primary="Doesn't close when you close RDP"
                  primaryTypographyProps={{ fontWeight: "medium" }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon sx={{ color: theme.palette.success.main }} />
                </ListItemIcon>
                <ListItemText
                  primary="Extremely cheap - highly recommended for multiple accounts - buy one VPS per account"
                  primaryTypographyProps={{ fontWeight: "medium" }}
                />
              </ListItem>
            </List>
          </Box>

          <Alert
            severity="warning"
            icon={<WarningIcon />}
            sx={{
              mb: 4,
              bgcolor: "rgba(255, 152, 0, 0.1)",
              border: "1px solid rgba(255, 152, 0, 0.3)",
              "& .MuiAlert-icon": {
                color: theme.palette.warning.main,
              },
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: "medium" }}>
              Up to 1-2 days after order to get the VPS infos. <strong>Make ticket if it is not setup</strong>
            </Typography>
          </Alert>

          <Divider sx={{ my: 4, borderColor: "rgba(255, 255, 255, 0.1)" }} />

          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
              <BuildIcon
                sx={{
                  fontSize: 30,
                  color: theme.palette.primary.main,
                }}
              />
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                Setup Instructions
              </Typography>
            </Box>

            <Card
              sx={{
                background: "rgba(0, 0, 0, 0.3)",
                border: "1px solid rgba(0, 229, 255, 0.2)",
                borderRadius: 2,
                mb: 3,
              }}
            >
              <CardContent>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <BuildIcon sx={{ color: theme.palette.primary.main }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Install Windows Server 2019"
                      primaryTypographyProps={{ fontWeight: "medium" }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <BuildIcon sx={{ color: theme.palette.primary.main }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                          <Typography component="span" sx={{ fontWeight: "medium" }}>
                            Install C++ when you first open VPS:
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={() =>
                              handleExternalNavigation(config.CPP_INSTALL_LINK, "Download C++ Redistributable")
                            }
                            sx={{
                              borderColor: theme.palette.primary.main,
                              color: theme.palette.primary.main,
                              "&:hover": {
                                borderColor: "#00e5ff",
                                backgroundColor: "rgba(0, 229, 255, 0.05)",
                                boxShadow: "0 0 10px rgba(0, 229, 255, 0.3)",
                              },
                            }}
                          >
                            vc_redist.x86.exe
                          </Button>
                        </Box>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <BuildIcon sx={{ color: theme.palette.primary.main }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Nothing else, you are ready to download the bot"
                      primaryTypographyProps={{ fontWeight: "medium" }}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Box>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
              <SpeedIcon
                sx={{
                  fontSize: 30,
                  color: theme.palette.primary.main,
                }}
              />
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                Recommended Performance Settings
              </Typography>
            </Box>

            <Card
              sx={{
                background: "rgba(0, 0, 0, 0.3)",
                border: "1px solid rgba(0, 229, 255, 0.2)",
                borderRadius: 2,
              }}
            >
              <CardContent>
                <Typography variant="h6" sx={{ color: theme.palette.primary.main, mb: 2 }}>
                  Low CPU Settings
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 1 }}>
                    <SettingsIcon sx={{ color: theme.palette.primary.main }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                      Config → Bot → Advanced:
                    </Typography>
                  </Box>
                  <Box component="ul" sx={{ pl: 4, mb: 0 }}>
                    <Typography component="li">
                      Ticks per second: <strong>100</strong>
                    </Typography>
                    <Typography component="li">
                      UI ticks per second: <strong>50</strong>
                    </Typography>
                    <Typography component="li">
                      Set to <strong>"Very Low (25%)"</strong>
                    </Typography>
                    <Typography component="li">Hide ships & projectiles</Typography>
                  </Box>
                </Box>

                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 1 }}>
                    <SettingsIcon sx={{ color: theme.palette.primary.main }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                      In-game settings:
                    </Typography>
                  </Box>
                  <Box component="ul" sx={{ pl: 4, mb: 0 }}>
                    <Typography component="li">Minimize the game window to make it very tiny</Typography>
                    <Typography component="li">Set all DarkOrbit settings to Low</Typography>
                    <Typography component="li">Hide game window</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Paper>
    </Container>
  )
}

export default VPS
